import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { events as eventsApi } from "../lib/api";
import { to12hr } from "../lib/format";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useReservationModal } from "../contexts/ReservationModalContext";

function dayOrdinal(n) {
  if (n >= 11 && n <= 13) return `${n}th`;
  const last = n % 10;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
}

function fmtCardDate(iso) {
  const d = new Date(iso + "T00:00:00");
  const wd = d.toLocaleDateString("en-US", { weekday: "short" });
  const mo = d.toLocaleDateString("en-US", { month: "short" });
  return `${wd}, ${mo} ${dayOrdinal(d.getDate())}`;
}

export default function EventsSection({ limit = 12 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { open: openReservation } = useReservationModal();
  const scrollerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    eventsApi.list("upcoming").then((data) => {
      setItems(data.slice(0, limit));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [limit]);

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    const cardWidth = scrollerRef.current.querySelector("[data-card]")?.offsetWidth || 340;
    scrollerRef.current.scrollBy({ left: dir * (cardWidth + 24), behavior: "smooth" });
  };

  return (
    <section data-testid="events-section" id="events" className="relative py-24 md:py-32 bg-white">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <span className="eyebrow">— What's On</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight text-ink">
              Upcoming <span className="text-ink">events</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="events-prev" onClick={() => scrollBy(-1)} className="w-11 h-11 border border-black/15 hover:border-ink hover:bg-ink hover:text-white text-ink transition flex items-center justify-center"><ChevronLeft size={18} /></button>
            <button data-testid="events-next" onClick={() => scrollBy(1)} className="w-11 h-11 border border-black/15 hover:border-ink hover:bg-ink hover:text-white text-ink transition flex items-center justify-center"><ChevronRight size={18} /></button>
            <button data-testid="events-view-all" onClick={() => navigate("/events")} className="btn-luxe btn-luxe-ghost ml-2 !py-2.5 !px-5 hidden sm:inline-flex">All Events</button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted italic text-center py-12">Loading the lineup…</p>
        ) : items.length === 0 ? (
          <p className="text-muted italic text-center py-12">No upcoming events. Check back soon.</p>
        ) : (
          <div
            ref={scrollerRef}
            className="flex gap-6 overflow-x-auto pb-4 -mx-6 px-6 md:-mx-10 md:px-10 lg:-mx-16 lg:px-16 snap-x snap-mandatory"
            style={{ scrollbarWidth: "thin" }}
          >
            {items.map((e) => (
              <div
                key={e.id}
                data-card
                data-testid={`event-card-${e.id}`}
                className="flex-shrink-0 w-[320px] md:w-[360px] snap-start group cursor-pointer flex flex-col"
                onClick={() => navigate(`/events/${e.id}`)}
              >
                {/* Flyer image — fixed ratio */}
                <div className="zoom-img bg-black aspect-[3/4] overflow-hidden flex-shrink-0">
                  {e.flyer_url ? (
                    <img src={e.flyer_url} alt={e.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 font-display text-3xl">Onyx</div>
                  )}
                </div>
                {/* Info panel — fixed structure so all cards align */}
                <div className="bg-ink text-white p-7 flex flex-col flex-1 min-h-[230px]">
                  <h3 className="font-display text-2xl md:text-[26px] leading-tight font-semibold mb-3 line-clamp-2 min-h-[64px]">{e.title}</h3>
                  <p className="text-white/90 text-[15px] mb-1">{fmtCardDate(e.date)}</p>
                  <p className="text-white/55 text-sm">{to12hr(e.start_time)} – {to12hr(e.end_time)}</p>
                  <button
                    data-testid={`event-book-${e.id}`}
                    onClick={(ev) => { ev.stopPropagation(); openReservation({ eventId: e.id }); }}
                    className="mt-auto w-full bg-[var(--onyx-gold)] hover:bg-[#9b7a3e] text-white font-medium text-[13px] tracking-luxe-tight uppercase py-3.5 transition flex items-center justify-center gap-2"
                  >
                    Book Now <span className="text-base">›</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 flex justify-center sm:hidden">
          <button onClick={() => navigate("/events")} className="btn-luxe btn-luxe-ghost">All Events</button>
        </div>
      </div>
    </section>
  );
}
